import {Injectable} from "@angular/core";
import {FormService} from "../../../umbrella-common/services/form.service";
import {AdditionalTransferQuestions, Order, Product} from "../../../store/state/order.state";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {UmbrellaValidators} from "../../../umbrella-common/validation/umbrella-validators";
import {dateWithoutTime} from "../../../umbrella-common/date-functions";
import {OrderActions} from "../../../store/actions/order.actions";
import {Store} from "@ngrx/store";
import {AppState} from "../../../store/state/app.state";
import {deviceType} from "../../../app.component";
import {takeWhile, tap} from "rxjs/operators";
import {TransferAuditDatesValidatorsService} from "./transfer-audit-dates-validators.service";
import {BaseOrderFormService} from "../common/base-order-form.service";
import {formatDate} from "../../../store/reducers/order.reducer";
import {sanitizeDate} from "../../../umbrella-common/common-functions";
import {TranslateService} from "@ngx-translate/core";
import {ValidationMsg} from "../../../umbrella-common/services/standard-error-map";
import moment from "moment";

@Injectable()
export class TransferDetailsFormService extends FormService implements BaseOrderFormService {

	private FORM_CONTROL_ERROR_MSG_MAP: { [validationKey: string]: ValidationMsg[] } = {
		lastAuditPerformedDateInFuture: [{msg: 'validations.transfer.not-future'}],
		lastAuditPerformedDateBeforeExpirationDate: [{msg: 'validations.transfer.before-certificate-expiration-date'}],
		expirationDateAfterLastAuditPerformedDate: [{msg: 'validations.transfer.after-last-performed-audit-date'}]
	};

	private readonly DEVICE_TYPE = deviceType;
	private transferReason: FormControl;
	private isCurrentCertificationBodyAccredited: FormControl;
	private hasOpenNonConformities: FormControl;
	private hasCorrectiveActionPlans: FormControl;
	private numberOfMajorNonConformities: FormControl;
	private numberOfMinorNonConformities: FormControl;
	private lastAuditPerformedDate: FormControl;
	private expirationDate: FormControl;
	private controlNamesInOrder: string[] = [];

	constructor(private fb: FormBuilder, private store: Store<AppState>, private transferAuditDatesValidators: TransferAuditDatesValidatorsService, translate: TranslateService) {
		super(translate);
	}

	protected buildFormFor(formKey: string, [product]: [Product]) {
		this.initControlNamesInOrder(product.standard.name);
		this.transferReason = this.fb.control(product.additionalQuestions.transferQuestions.transferReason, [Validators.required, UmbrellaValidators.textLengthBetween(3, 5000)]);
		this.isCurrentCertificationBodyAccredited = this.fb.control(product.additionalQuestions.transferQuestions.isCurrentCertificationBodyAccredited, [Validators.required]);
		this.hasOpenNonConformities = this.fb.control(product.additionalQuestions.transferQuestions.hasOpenNonConformities, [Validators.required]);
		this.hasCorrectiveActionPlans = this.fb.control(product.additionalQuestions.transferQuestions.hasCorrectiveActionPlans, [Validators.required]);
		this.numberOfMajorNonConformities = this.fb.control(product.additionalQuestions.transferQuestions.numberOfMajorNonConformities, [Validators.required, Validators.min(0), Validators.max(99)]);
		this.numberOfMinorNonConformities = this.fb.control(product.additionalQuestions.transferQuestions.numberOfMinorNonConformities, [Validators.required, Validators.min(0), Validators.max(99)]);
		this.lastAuditPerformedDate = this.fb.control(this.getDate(product.additionalQuestions.transferQuestions.lastAuditPerformedDate));
		this.expirationDate = this.fb.control(this.getDate(product.additionalQuestions.transferQuestions.expirationDate));

		const form = this.fb.group({});

		form.addControl('transferReason' + product.standard.name, this.transferReason);
		form.addControl('isCurrentCertificationBodyAccredited' + product.standard.name, this.isCurrentCertificationBodyAccredited);
		form.addControl('hasOpenNonConformities' + product.standard.name, this.hasOpenNonConformities);
		form.addControl('lastAuditPerformedDate' + product.standard.name, this.lastAuditPerformedDate);
		form.addControl('expirationDate' + product.standard.name, this.expirationDate);

		if (product.additionalQuestions.transferQuestions.hasOpenNonConformities) {
			form.addControl('hasCorrectiveActionPlans' + product.standard.name, this.hasCorrectiveActionPlans);
			form.addControl('numberOfMajorNonConformities' + product.standard.name, this.numberOfMajorNonConformities);
			form.addControl('numberOfMinorNonConformities' + product.standard.name, this.numberOfMinorNonConformities);
		}

		form.get('lastAuditPerformedDate' + product.standard.name).setValidators(this.transferAuditDatesValidators.lastAuditPerformedDateValidator(form, product.standard.name));
		form.get('expirationDate' + product.standard.name).setValidators(this.transferAuditDatesValidators.expirationDateValidator(form, product.standard.name));

		this.initFormControls(form, product);
		this.initializeHasOpenNonConformitiesControls(product.additionalQuestions.transferQuestions.hasOpenNonConformities, form, product);

		return form;
	}

	fillOrder(order: Order): Order {
		this.getAllFormKeys().forEach(standard => {
			let product = order.products.find(p => p.standard.name == standard);
			product.additionalQuestions.transferQuestions = {
				transferReason: this.getValueOrNull(standard, 'transferReason'),
				isCurrentCertificationBodyAccredited: this.getValueOrNull(standard, 'isCurrentCertificationBodyAccredited'),
				hasOpenNonConformities: this.getValueOrNull(standard, 'hasOpenNonConformities'),
				lastAuditPerformedDate: this.formatAndSanitizeDate(this.getValueOrNull(standard, 'lastAuditPerformedDate')),
				expirationDate: this.formatAndSanitizeDate(this.getValueOrNull(standard, 'expirationDate'))
			} as AdditionalTransferQuestions;

			if (!!this.getValueOrNull(standard, 'hasOpenNonConformities')) {
				product.additionalQuestions.transferQuestions.hasCorrectiveActionPlans = this.getValueOrNull(standard, 'hasCorrectiveActionPlans');
				product.additionalQuestions.transferQuestions.numberOfMajorNonConformities = this.getValueOrNull(standard, 'numberOfMajorNonConformities');
				product.additionalQuestions.transferQuestions.numberOfMinorNonConformities = this.getValueOrNull(standard, 'numberOfMinorNonConformities');
			}
		})

		return order;
	}

	private formatAndSanitizeDate(date: Date): Date {
		// @ts-ignore
		return formatDate(sanitizeDate(date));
	}

	getControlNamesInOrder(): string[] {
		return this.controlNamesInOrder;
	}

	private initFormControls(form: FormGroup, product: Product) {
		const standardName = product.standard.name;
		form.controls['transferReason' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
			.subscribe(transferReason => this.store.dispatch(OrderActions.SET_TRANSFER_REASON({standardName, transferReason})));
		form.controls['isCurrentCertificationBodyAccredited' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
			.subscribe(isCurrentCertificationBodyAccredited => this.store.dispatch(OrderActions.SET_IS_CURRENT_CERTIFICATION_BODY_ACCREDITED({standardName, isCurrentCertificationBodyAccredited})));
		form.controls['hasOpenNonConformities' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
			.subscribe(hasOpenNonConformities => this.updateHasOpenNonConformities(hasOpenNonConformities, form, product));
		form.controls['lastAuditPerformedDate' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), tap(value => {
			if (value != null && value === '') {
				this.updateFormControl('lastAuditPerformedDate', null, product.standard.name); // FIX for android native datepicker 'clear' option
			}
		})).subscribe(lastAuditPerformedDate => this.store.dispatch(OrderActions.SET_LAST_AUDIT_PERFORMED_DATE({standardName, lastAuditPerformedDate})));

		form.controls['expirationDate' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)), tap(value => {
			if (value != null && value === '') {
				this.updateFormControl('expirationDate', null, product.standard.name);
			}
		})).subscribe(expirationDate => this.store.dispatch(OrderActions.SET_EXPIRATION_DATE({standardName, expirationDate})));
	}

	private updateHasOpenNonConformities(hasOpenNonConformities: boolean, form: FormGroup, product: Product) {
		const standardName = product.standard.name;
		if (hasOpenNonConformities) {
			this.addControlIfNotPresent(form, standardName, 'hasCorrectiveActionPlans', this.fb.control(null, [Validators.required]));
			this.addControlIfNotPresent(form, standardName, 'numberOfMajorNonConformities', this.fb.control(null, [Validators.required, Validators.min(0), Validators.max(99)]));
			this.addControlIfNotPresent(form, standardName, 'numberOfMinorNonConformities', this.fb.control(null, [Validators.required, Validators.min(0), Validators.max(99)]));
			form.controls['hasCorrectiveActionPlans'  + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
				.subscribe(hasCorrectiveActionPlans => this.store.dispatch(OrderActions.SET_HAS_CORRECTIVE_ACTION_PLANS({standardName, hasCorrectiveActionPlans})));
			form.controls['numberOfMajorNonConformities' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
				.subscribe(numberOfMajorNonConformities => this.store.dispatch(OrderActions.SET_NUMBER_OF_MAJOR_NON_CONFORMITIES({standardName, numberOfMajorNonConformities})));
			form.controls['numberOfMinorNonConformities' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
				.subscribe(numberOfMinorNonConformities => this.store.dispatch(OrderActions.SET_NUMBER_OF_MINOR_NON_CONFORMITIES({standardName, numberOfMinorNonConformities})));
		} else {
			this.removeControlIfPresent(standardName, 'hasCorrectiveActionPlans' + product.standard.name);
			this.removeControlIfPresent(standardName, 'numberOfMajorNonConformities' + product.standard.name);
			this.removeControlIfPresent(standardName, 'numberOfMinorNonConformities' + product.standard.name);
			this.store.dispatch(OrderActions.SET_HAS_CORRECTIVE_ACTION_PLANS({standardName, hasCorrectiveActionPlans: null}));
			this.store.dispatch(OrderActions.SET_NUMBER_OF_MAJOR_NON_CONFORMITIES({standardName, numberOfMajorNonConformities: null}));
			this.store.dispatch(OrderActions.SET_NUMBER_OF_MINOR_NON_CONFORMITIES({standardName, numberOfMinorNonConformities: null}));
		}
		this.store.dispatch(OrderActions.SET_HAS_OPEN_NON_CONFORMITIES({standardName, hasOpenNonConformities}));
	}

	private initializeHasOpenNonConformitiesControls(hasOpenNonConformities: boolean, form: FormGroup, product: Product) {
		if (hasOpenNonConformities) {
			const standardName = product.standard.name;
			this.addControlIfNotPresent(form, standardName, 'hasCorrectiveActionPlans', this.fb.control(product.additionalQuestions.transferQuestions.hasCorrectiveActionPlans, [Validators.required]));
			this.addControlIfNotPresent(form, standardName, 'numberOfMajorNonConformities', this.fb.control(product.additionalQuestions.transferQuestions.numberOfMajorNonConformities, [Validators.required, Validators.min(0), Validators.max(99)]));
			this.addControlIfNotPresent(form, standardName, 'numberOfMinorNonConformities', this.fb.control(product.additionalQuestions.transferQuestions.numberOfMinorNonConformities, [Validators.required, Validators.min(0), Validators.max(99)]));
			form.controls['hasCorrectiveActionPlans' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
				.subscribe(hasCorrectiveActionPlans => this.store.dispatch(OrderActions.SET_HAS_CORRECTIVE_ACTION_PLANS({standardName, hasCorrectiveActionPlans})));
			form.controls['numberOfMajorNonConformities'  + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name)))
				.subscribe(numberOfMajorNonConformities => this.store.dispatch(OrderActions.SET_NUMBER_OF_MAJOR_NON_CONFORMITIES({standardName, numberOfMajorNonConformities})));
			form.controls['numberOfMinorNonConformities' + product.standard.name].valueChanges.pipe(takeWhile(() => this.isSubFormInitialized(product.standard.name))).subscribe(numberOfMinorNonConformities => this.store.dispatch(OrderActions.SET_NUMBER_OF_MINOR_NON_CONFORMITIES({standardName, numberOfMinorNonConformities})));
		}
	}

	private addControlIfNotPresent(form: FormGroup, formKey: string, controlName: string, control: FormControl) {
		controlName = controlName.endsWith(formKey) ? controlName : controlName + formKey;
		if (!form.controls[controlName]) {
			this.subFormMap[formKey].addControl(controlName, control);
		}
	}

	private removeControlIfPresent(formKey: string, controlName: string) {
		const formControl = this.getFormControl(formKey, controlName);
		if (formControl) {
			formControl.setValue(undefined);
			formControl.markAsPristine();
			this.subFormMap[formKey].removeControl(controlName);
		}
	}

	protected override getFormSpecificErrorMap(): { [p: string]: ValidationMsg[] } {
		return this.FORM_CONTROL_ERROR_MSG_MAP;
	}

	override getAllFormKeys(): string[] {
		return Object.keys(this.subFormMap);
	}

	protected getDate(date: Date | string) {
		if (date) {
			return moment(dateWithoutTime(date));
		}

		return null;
	}

	private initControlNamesInOrder(formKey: string) {
		this.controlNamesInOrder = this.controlNamesInOrder.concat(['transferReason' + formKey, 'isCurrentCertificationBodyAccredited' + formKey, 'hasOpenNonConformities' + formKey,
			'hasCorrectiveActionPlans' + formKey, 'numberOfMinorNonConformities' + formKey, 'numberOfMajorNonConformities' + formKey, 'lastAuditPerformedDate' + formKey,
			'expirationDate' + formKey]);
	}
}
